import { useMemo, useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { Card } from "@/clientWeb/components/ui/card"
import { Button } from "@/clientWeb/components/ui/button"
import { Eye, Filter, RefreshCw } from "lucide-react"
import { MeetingListSkeleton } from "@/clientWeb/components/ui/skeleton-loaders"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/clientWeb/components/ui/tooltip"
import { useMeetingsStore } from "../../store/meetings.store"
import "./MeetingList.css"
import { formatMeetingTime, meetingZoneTimeSuffix } from "../../services/meetings.service"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { isMeetingHost } from "../../services/meeting-host"
import { joinLockedUntil, joinPathId } from "../../utils/recurrence"
import { JoinLockHint } from "../JoinLockHint"
import { InviteesCard } from "./InviteesCard"

/* ─── Avatar Colors ───────────────────────────────────────────────────── */
const PASTEL_COLORS = [
  "#F4D5DB", // pastel pink
  "#D5E3F4", // pastel blue
  "#D5F4D5", // pastel green
  "#E3D5F4", // pastel purple
  "#F4E3D5", // pastel orange
]

function getAvatarColor(name: string) {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return PASTEL_COLORS[Math.abs(hash) % PASTEL_COLORS.length]
}

/* ─── Sub-components ───────────────────────────────────────────────────── */

function Avatar({ name = "", seed, size = 28 }: { name: string; seed?: string; size?: number }) {
  // `name` is typed as required, but real data disagrees often enough that this
  // component crashed the whole list on it (both `getAvatarColor`'s unguarded
  // `.length` and the `.split().map()` chain below throw on undefined) — the
  // default above covers both call sites in one place instead of `?.`-patching each.
  const color = getAvatarColor(seed ?? name)
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: color, 
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 600, color: "rgba(0,0,0,0.5)",
      flexShrink: 0,
    }}>{initials}</div>
  )
}

function getStatusBorderColor(status: string) {
  const map: Record<string, string> = {
    ongoing: "border-l-emerald-500",
    upcoming: "border-l-amber-500",
    completed: "border-l-slate-400",
    expired: "border-l-slate-400",
  }
  return map[status] ?? map.completed
}

/** Expired meetings are past meetings — treat them as completed in the UI. */
function isPastMeeting(status: string) {
  return status === "completed" || status === "expired"
}


export default function MeetingList({
  onMeetingClick,
  selectedDate,
}: {
  onMeetingClick: (meeting: any) => void
  selectedDate?: Date
}) {
  const navigate = useNavigate()
  const t = useTranslation()
  const authUser = useAuthStore((s) => s.user)
  const meetings = useMeetingsStore((s) => s.meetings)
  const refreshStatuses = useMeetingsStore((s) => s.refreshStatuses)
  const fetchFromApi = useMeetingsStore((s) => s.fetchFromApi)
  const [activeTab, setActiveTab] = useState<"meetings" | "shared">("meetings")
  const [filter, setFilter] = useState<"all" | "upcoming" | "ongoing" | "completed">("all")
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Filtering is a local array operation over data already in memory, so there
  // is nothing to wait for. This used to force an 800ms skeleton on every
  // filter or tab change: the list blanked, the result arrived nearly a second
  // later, and clicking through the filters read as a control that did not
  // work. The skeleton belongs to an actual fetch, and now only appears for one.
  useEffect(() => {
    refreshStatuses()
    setIsLoading(false)
  }, [filter, activeTab, refreshStatuses])

  // Refresh now asks the server.
  //
  // It used to call `refreshStatuses()` — which only re-derives status from the
  // times already in the store — and then wait a second so it looked like work
  // had happened. A meeting created on another device, or by anyone else, never
  // appeared however many times this was pressed.
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    setIsLoading(true)
    try {
      await fetchFromApi()
      refreshStatuses()
      toast.success({ title: t("meetings.refreshed") })
    } finally {
      setIsRefreshing(false)
      setIsLoading(false)
    }
  }, [fetchFromApi, refreshStatuses, t])

  // The meetings on the selected day (and tab), before the status filter. The
  // header counts are taken from this, not from `meetings`: they used to count
  // every meeting in the store, so a day with one completed meeting read
  // "Completed(16)" — the user's whole history.
  const dayMeetings = useMemo(() => {
    const isSameDay = (aMs: number, d: Date) => {
      const a = new Date(aMs)
      return (
        a.getFullYear() === d.getFullYear() &&
        a.getMonth() === d.getMonth() &&
        a.getDate() === d.getDate()
      )
    }

    const dateFiltered = selectedDate
      ? meetings.filter((m) => isSameDay(m.startTimeMs, selectedDate))
      : meetings

    return activeTab === "shared"
      ? dateFiltered.filter(m => m.type === 'joined')
      : dateFiltered
  }, [activeTab, meetings, selectedDate])

  const ongoing   = useMemo(() => dayMeetings.filter(m => m.status === "ongoing").length,   [dayMeetings])
  const upcoming  = useMemo(() => dayMeetings.filter(m => m.status === "upcoming").length,  [dayMeetings])
  const completed = useMemo(() => dayMeetings.filter(m => isPastMeeting(m.status)).length,  [dayMeetings])

  const filteredMeetings = useMemo(() => {
    const scoped = filter === "all"
      ? dayMeetings
      : filter === "completed"
        ? dayMeetings.filter(m => isPastMeeting(m.status))
        : dayMeetings.filter(m => m.status === filter)
    // Ongoing/upcoming meetings sort ahead of completed/expired ones, so the
    // current or next-upcoming meeting always leads the list; each group is
    // then ordered chronologically by its own scheduled start time.
    return [...scoped].sort((a, b) => {
      const pastRank = Number(isPastMeeting(a.status)) - Number(isPastMeeting(b.status))
      return pastRank !== 0 ? pastRank : a.startTimeMs - b.startTimeMs
    })
  }, [dayMeetings, filter])

  return (
    <Card className="h-full flex flex-col">
      <div className="flex flex-col gap-2 p-4 h-full overflow-hidden">
        {/* Header */}
        <div className="flex justify-between animate-fade-slide-up flex-shrink-0" style={{ animation: "fadeSlideUp 0.4s ease 0.1s both" }}>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-bold text-slate-900 dark:text-foreground/80 dark:text-foreground">
                {t("meetings.today")} ({filteredMeetings.length})
              </h2>
              <div className="hidden lg:flex items-center gap-3">
                {[ 
                  { color: "bg-emerald-500", label: `${t("meetings.ongoing")}(${ongoing})` },
                  { color: "bg-amber-500", label: `${t("meetings.upcoming")}(${upcoming})` },
                  { color: "bg-slate-400", label: `${t("meetings.completed")}(${completed})` },
                ].map(({ color, label }) => (
                  <div key={label} className="flex items-center gap-1">
                    <div className={`w-1.5 h-1.5 rounded-full ${color}`} />
                    <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Tabs */}
            {/* <div className="flex items-center mt-2 gap-2 bg-muted/70 dark:bg-card rounded-lg p-1 w-fit">
              {[{ key: "meetings", label: t("meetings.myMeetings") }, { key: "shared", label: t("meetings.sharedMeetings") }].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as "meetings" | "shared")}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                    activeTab === tab.key ? "bg-card shadow-sm text-primary dark:text-foreground dark:bg-secondary-100/10" : "text-slate-600 dark:text-slate-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div> */}
          </div>

          {/* Filter Dropdown & Refresh */}
          <div className="flex gap-1">
               <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handleRefresh}
                  className="text-slate-600 dark:text-slate-300"
                >
                  <RefreshCw 
                    className={`w-4 h-4 transition-transform ${isRefreshing ? 'animate-spin' : ''}`} 
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">{t("meetings.refresh")}</TooltipContent>
            </Tooltip>
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative">
                      <Filter className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                      {filter !== "all" && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full" />}
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="top">{
                  [
                    { key: "upcoming", label: t("meetings.upcoming") },
                    { key: "ongoing", label: t("meetings.ongoing") },
                    { key: "completed", label: t("meetings.completed") },
                    { key: "all", label: t("common.all") },
                  ].find(f => f.key === filter)?.label ?? ""
                }</TooltipContent>
              </Tooltip>
            <DropdownMenuContent align="end" className="min-w-[150px]">
              {[ 
                { key: "all", label: t("common.all") },
                { key: "upcoming", label: t("meetings.upcoming") },
                { key: "ongoing", label: t("meetings.ongoing") },
                { key: "completed", label: t("meetings.completed") },
              ].map(item => (
                <DropdownMenuItem
                  key={item.key}
                  onClick={() => setFilter(item.key as any)}
                  className={filter === item.key ? "font-semibold text-primary" : ""}
                >
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <MeetingListSkeleton count={4} />
          ) : filteredMeetings.length === 0 ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-6 text-center">
              <img
                src="/icons/noMeetingsNew copy.svg"
                alt="No meetings"
                className="w-52 max-w-full drop-shadow-sm zoom-animation"
              />
              <div className="space-y-1">
                <p className="text-base font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">
                  {activeTab === "shared" ? t("meetings.noSharedFound") : t("meetings.noMeetingsFilter")}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {activeTab === "shared" ? t("meetings.tryAdjustFilters") : t("meetings.tryChangingFilter")}
                </p>
              </div>
            </div>
          ) : (
            filteredMeetings.map((m, i) => (
              <div
                key={m.id}
                onClick={() => onMeetingClick(m)}
                className={`flex items-center gap-3 p-3 shadow-sm rounded-[14px] bg-card transition-all duration-180 cursor-pointer animate-fade-slide-up hover:border-indigo-100 dark:hover:border-indigo-400 hover:bg-indigo-50/30 dark:hover:bg-card hover:shadow-sm mb-3 ${getStatusBorderColor(m.status)}`}
                style={{ animationDelay: `${0.15 + i * 0.08}s`, animation: "fadeSlideUp 0.4s ease both" }}
              >
                {/* Avatar */}
                <Avatar name={m.attendees[0]} seed={`${m.id}-${m.host}`} size={40} />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-xs text-slate-400 dark:text-foreground">
                      {formatMeetingTime(m.startTimeMs)} to {formatMeetingTime(m.endTimeMs)}
                      {meetingZoneTimeSuffix(m.startTimeMs, m.timezone)}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-start text-slate-900 dark:text-foreground/80 dark:text-white mb-0.25 whitespace-nowrap overflow-hidden text-ellipsis">{m.title}</span>
                    <span className="text-xs text-start text-slate-400 dark:text-foreground">{m.host}</span>
                  </div>
                </div>

                {/* Attendee avatars: hover or click for everyone invited */}
                <div className="flex items-center">
                  <InviteesCard meeting={m} avatarColor={getAvatarColor}>
                  <div className="flex">
                    {m.attendees.slice(0, 3).map((att, j) => {
                      const color = getAvatarColor(`${m.id}-${att}-${j}`)
                      return (
                        <div key={j} className="w-6.5 h-6.5 rounded-full flex items-center justify-center text-[9px] font-bold border-2 border-card dark:border-border" style={{
                        background: color,
                        color: "rgba(0,0,0,0.5)",
                        marginLeft: j > 0 ? "-8px" : "0"
                      }}>
                          {att}
                        </div>
                      )
                    })}
                    {m.attendees.length > 3 && (
                      <div className="w-6.5 h-6.5 rounded-full bg-slate-100 dark:bg-slate-700 border-2 border-card dark:border-slate-600 flex items-center justify-center text-[9px] font-bold text-slate-500 dark:text-slate-400" style={{ marginLeft: "-8px" }}>
                        +{m.attendees.length - 3}
                      </div>
                    )}
                  </div>
                  </InviteesCard>
                </div>

                {/* Action button based on status */}
                {m.status === 'ongoing' && (
                  <Button
                    size={'sm'}
                    onClick={(e) => { e.stopPropagation(); navigate(`/meeting/${joinPathId(m)}?name=${encodeURIComponent(m.host)}`) }}
                  >
                    {t("meetings.joinNow")}
                  </Button>
                )}
                {m.status === 'upcoming' && (
                  // A later day of a series cannot be joined yet: say when, rather than refuse on click.
                  <JoinLockHint lockedUntilMs={joinLockedUntil(m)}>
                  {(m.canModerate ?? isMeetingHost(authUser, m)) ? (
                    <Button
                      size={'sm'}
                      disabled={joinLockedUntil(m) !== null}
                      onClick={(e) => { e.stopPropagation(); navigate(`/meeting/${joinPathId(m)}?name=${encodeURIComponent(m.host)}`) }}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium px-4 py-2 h-auto rounded-lg"
                    >
                      {t("meetings.startMeeting")}
                    </Button>
                  ) : (
                    <Button
                      size={'sm'}
                      disabled={joinLockedUntil(m) !== null}
                      onClick={(e) => { e.stopPropagation(); navigate(`/meeting/${joinPathId(m)}?name=${encodeURIComponent(m.host)}`) }}
                    >
                      {t("meetings.join")}
                    </Button>
                  )}
                  </JoinLockHint>
                )}
                {isPastMeeting(m.status) && (
                  <Button
                    // onClick={(e) => { e.stopPropagation(); navigate(`/meetings/${m.id}/past`) }}
                    variant={'ghost'}
                    className="text-primary dark:text-foreground"
                    title={t('meetings.viewPastMeeting')}
                  >
                    <Eye className="w-4 h-4"/>
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </Card>
  )
}
